/*eslint new-cap:0 */
import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';

import ViewModel from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/expandedTextAd';
import editorTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/expandedTextAd.pug';
import { isCampaignEditorialError, isCampaignApiError } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import CampaignEditorialError from '@bingads-webui-component-legacy/api-errors-campaign/editorial-error';
import buildPreview from '@bingads-webui-campaign-legacy/crud-ad/view/adsPreview/expandedTextAd';
import uuid from '@bingads-webui-component-legacy/uuid/index';
import UrlEditor from '@bingads-webui-component-legacy/url-input/view/url-input';
import UrlOptions from '@bingads-webui-component-legacy/upgraded-url/view/urlOptionsV2';
import legacyInstru from '@bingads-webui-static-legacy/instrumentation/index';
import constants from '@bingads-webui-static-legacy/instrumentation/constants';
import { enableAdCustomizersHelpers } from './adCustomizersHelpers';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const permissions = config.get('Permissions') || {};

const fieldsWithPlaceHolder = ['TitlePart1', 'TitlePart2', 'TitlePart3', 'Text', 'Path1', 'Path2', 'FinalUrls', 'MobileFinalUrls']
  .concat(permissions.IsVanityUrlEnabled ? ['DisplayUrl'] : []);
const placeholders = _.object(fieldsWithPlaceHolder, _.map(fieldsWithPlaceHolder, (field) => {
  return i18n.get(`ExpandedTextAd_Placeholder_${field}`);
}));
const fieldsWithLabel = ['Path1', 'Path2'];
const labels = _.object(fieldsWithLabel, _.map(fieldsWithLabel, (field) => {
  return i18n.get(`ExpandedTextAd_Label_${field}`);
}));
const {
  IsExpandedTextAdV2Enabled,
  IsDisclaimerAdsEnabled,
  IsEXTAMigrationNotificationEnabled,
} = permissions;


/*For initial stage, MT converts the expanded text ad to standard text ad and passes it to editorial.
  So editorial will still return adtitle instead of titlepart1/titlepart2 as it doesn't know anything about EXTA.
  Once editorial is on boarded, will remove following mapping,
*/
const fieldMapping = {
  'Title': 'TitlePart1',
  'DisplayUrl': 'Path1',
  'DestinationUrl': 'FinalUrls',
};

const nameAttrs = {
  'TitlePart1': 'Ad.ExpandedTextAd.TitlePart1',
  'TitlePart2': 'Ad.ExpandedTextAd.TitlePart2',
  'TitlePart3': 'Ad.ExpandedTextAd.TitlePart3',
  'Text': 'Ad.ExpandedTextAd.AdDescription',
  'TextPart2': 'Ad.ExpandedTextAd.AdDescription2',
  'Path1': 'Ad.ExpandedTextAd.Path1',
  'Path2': 'Ad.ExpandedTextAd.Path2',
  'NativePreference': 'Ad.ExpandedTextAd.IsNativeNetworkPreferred',
};

export default class View extends Backbone.View {
  initialize({
    showInline = false,
    editMode = false,
    model = null,
    isForVNext = false,
    isNativeAdsEnabled = permissions.IsNativeAdsAllowed,
    isVanityUrlEnabled = permissions.IsVanityUrlEnabled,
    isTraditionalChinese = false,
    defaultLandingPageUrl = '',
    activityLogger,
    scope = null,
    perfMarker,
  } = {}) {
    this.isForVNext = isForVNext;
    this.showInline = showInline;
    this.editMode = editMode;
    this.isNativeAdsEnabled = isNativeAdsEnabled;
    this.isVanityUrlEnabled = isVanityUrlEnabled;
    this.isNativeAdsRebrandingEnabled = permissions.IsNativeAdsRebrandingEnabled;
    this.defaultLandingPageUrl = defaultLandingPageUrl;
    this.activityLogger = activityLogger;
    if (perfMarker) {
      this.perfMarker = perfMarker.createChild('Load_Audience_List');
    }
    this.viewModel = new ViewModel({
      model,
      isNativeAdsEnabled,
      isVanityUrlEnabled,
      isTraditionalChinese,
      placeholders,
      defaultLandingPageUrl,
      IsExpandedTextAdV2Enabled,
      scope,
      activityLogger,
      perfMarker: this.perfMarker,
    });

    this.finalUrlEditor = new UrlEditor({
      url: _.first(_.result(this.model, 'FinalUrls')),
      ariaLabelledby: 'extaFinalUrlLabel',
      ariaId: 'extaFinalUrl',
      required: true,
    });
    this.mobileUrlEditor = new UrlEditor({
      url: _.first(_.result(this.model, 'MobileFinalUrls')),
      ariaLabelledby: 'extaMobileUrlLabel',
      ariaId: 'extaMobileUrl',
    });

    this.finalUrlEditor.on('urlChanged', (url) => {
      this.viewModel.FinalUrls(url ? [url] : []);
    });
    this.mobileUrlEditor.on('urlChanged', (url) => {
      this.viewModel.MobileFinalUrls(url ? [url] : []);
    });

    this.viewModel.FinalUrls.errors.subscribe((value) => {
      this.finalUrlEditor.hasErrors(!_.isEmpty(value));
    });

    this.viewModel.MobileFinalUrls.errors.subscribe((value) => {
      this.mobileUrlEditor.hasErrors(!_.isEmpty(value));
    });

    this.urlOptions = new UrlOptions({
      isForVNext,
      placeholderEnabled: true,
      showUurlTestTool: true,
      trackingTemplate: _.result(this.model, 'TrackingUrlTemplate'),
      finalUrlSuffix: _.result(this.model, 'FinalUrlSuffix'),
      customParameters: _.isEmpty(_.result(this.model, 'UrlCustomParameters')) ? undefined : _.clone(this.model.UrlCustomParameters),
      activityLogger,
    });

    this.urlOptions.on('testUurl', (fn, originalView) => {
      this.trigger('testUurl', fn, originalView);
    });

    return this;
  }

  get events() {
    return {
      'click .prefer-native-format': 'preferNativeAdFormat',
    };
  }

  render() {
    this.$el.html(editorTemplate({
      i18n,
      uuid,
      isNativeAdsEnabled: this.isNativeAdsEnabled,
      isVanityUrlEnabled: this.isVanityUrlEnabled,
      isNativeAdsRebrandingEnabled: this.isNativeAdsRebrandingEnabled,
      nameAttrs,
      labels,
      placeholders: {},
      domainWatermark: placeholders.FinalUrls,
      IsExpandedTextAdV2Enabled,
      IsDisclaimerAdsEnabled,
      IsEXTAMigrationNotificationEnabled,
    }));

    ko.applyBindings(this.viewModel, this.el);

    this.$('.final-url').empty().append(this.finalUrlEditor.render().el);
    this.$('.mobile-url').empty().append(this.mobileUrlEditor.render().el);
    this.$('.ext-ad-url-options').empty().append(this.urlOptions.render().el);

    this.$('.final-url input.url-field').attr('name', 'FinalUrls');
    this.$('.mobile-url input.url-field').attr('name', 'MobileFinalUrls');

    this.previewContainer = buildPreview(this.viewModel);
    this.$('.preview-container').empty().append(this.previewContainer.render().el);
    this.previewContainer.selectedPanel(this.viewModel.initPreviewTab());

    this.enableAdCustomizersHelpersForEXTA();
    return this;
  }

  preferNativeAdFormat() {
    this.previewContainer.selectedPanel(this.viewModel.showNativeAdPreview() ? 'native' : 'top');
  }

  validateViewModel() {
    const isValid = this.viewModel.validate();

    if (!isValid) {
      _.each(this.viewModel.fields, (field) => {
        if (!this.viewModel[field].isValid()) {
          if (this.activityLogger) {
            this.activityLogger.signal(`ad field invalid: ${field}`, 'expanded text ad editor');
          }
        }
      });
    }
    return isValid;
  }

  validate() {
    return _.reduce([
      this.validateViewModel(),
      _.isEmpty(this.viewModel.errorMessage()),
      this.urlOptions ? this.urlOptions.isValid() : true,
    ], (prev, next) => {
      return prev && next;
    }, true);
  }

  resetEditMode(mode) {
    this.editMode = mode;
  }

  toObject() {
    const params = _.omit(_.extend(this.viewModel.toObject(), this.urlOptions ? this.urlOptions.toObject() : {}),
      this.isVanityUrlEnabled ? [] : ['DisplayUrl']);

    const api = this.showInline ? 'odata' : 'bulk';
    const arrayEmptyValue = api === 'odata' ? [] : null;
    const stringEmptyValue = api === 'odata' ? '' : null;

    /* 1.Replace all line breaks with spaces 2. Remove whitespace from both sides */
    const regex = /(?:\r\n|\r|\n)/g;

    params.Text = params.Text.replace(regex, ' ').trim();
    if (IsExpandedTextAdV2Enabled && params.TextPart2) {
      params.TextPart2 = params.TextPart2.replace(regex, ' ').trim();
    }

    if (params.TextPart2 === undefined || params.TextPart2 === null) {
      legacyInstru.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO,
        'crud-ad/view/adEditor',
        `IsExpandedTextAdV2Enabled: ${IsExpandedTextAdV2Enabled},
        TextPart2: ${this.viewModel.TextPart2 ? this.viewModel.TextPart2() : 'Not in viewModel'},
        model: ${this.model ? this.model.TextPart2 : 'model is not set'}`);
    }

    //bulk API needs use 'delete_vlaue' to indicate deleting a value, in jsdata bulk adapter, we will replace null with 'delete_value' to remove value
    if (this.editMode) {
      const nullableArrayFields = [
        'FinalUrls',
        'MobileFinalUrls',
        'UrlCustomParameters',
      ];

      const nullableStringFields = _.compact([
        'TrackingUrlTemplate',
        'FinalUrlSuffix',
        'Path1',
        'Path2',
      ]);

      _.each(nullableArrayFields, (prop) => {
        if (_.isEmpty(params[prop])) {
          params[prop] = arrayEmptyValue;
        }
      });

      _.each(nullableStringFields, (prop) => {
        if (_.isEmpty(params[prop])) {
          params[prop] = stringEmptyValue;
        }
      });
    }

    return params;
  }

  handleApiErrors(errors) {
    const title1 = this.viewModel.TitlePart1();
    const title2 = this.viewModel.TitlePart2();
    const title3 = IsExpandedTextAdV2Enabled ? this.viewModel.TitlePart3() : '';

    const parsedErrors = _.reduce(errors, (memo, error) => {
      if (_.has(error, 'property') && isCampaignEditorialError(error)) {
        const disapproved = _.result(error, 'disapprovedText', '');

        if (error.property === 'Title') {
          let isTitle1HaveErrors = title1.indexOf(disapproved) > -1;
          let isTitle2HaveErrors = title2.indexOf(disapproved) > -1;
          const { message, property, code, disapprovedText, isAppealable } = error;

          if (IsExpandedTextAdV2Enabled && title3) {
            let isTitle3HaveErrors = title3.indexOf(disapproved) > -1;

            if (!isTitle1HaveErrors && !isTitle2HaveErrors && !isTitle3HaveErrors) {
              if (`${title1} ${title2}`.indexOf(disapproved) > -1) {
                // error in (title1 + title2)
                isTitle1HaveErrors = true;
                isTitle2HaveErrors = true;
              } else if (`${title2} ${title3}`.indexOf(disapproved) > -1) {
                // error in (title2 + title3)
                isTitle2HaveErrors = true;
                isTitle3HaveErrors = true;
              } else {
                // If (title1 + title2 + title3) contain disapproved text, show errors on all fields.
                isTitle1HaveErrors = true;
                isTitle2HaveErrors = true;
                isTitle3HaveErrors = true;
              }
            }

            const returnError = [...memo];

            if (isTitle1HaveErrors) {
              returnError.push(_.extend(error, { property: 'TitlePart1' }));
            }

            if (isTitle2HaveErrors) {
              returnError.push(_.extend(new CampaignEditorialError(message, property, code, disapprovedText, isAppealable), { property: 'TitlePart2' }));
            }

            if (isTitle3HaveErrors) {
              returnError.push(_.extend(new CampaignEditorialError(message, property, code, disapprovedText, isAppealable), { property: 'TitlePart3' }));
            }
            return returnError;
          } else if ((isTitle1HaveErrors && isTitle2HaveErrors) || (!isTitle1HaveErrors && !isTitle2HaveErrors)) {
            // Both title1 and title2 contain disapprovedText, or (title1 + title2) contain disapprovedText, show errors
            // on both fields
            return [...memo, _.extend(error, {
              property: 'TitlePart1',
            }), _.extend(new CampaignEditorialError(message, property, code, disapprovedText, isAppealable), {
              property: 'TitlePart2',
            })];
          } else {
            return [...memo, _.extend(error, {
              property: isTitle2HaveErrors ? 'TitlePart2' : 'TitlePart1',
            })];
          }
        } else if (error.property === 'Text' && IsExpandedTextAdV2Enabled) {
          const text1 = this.viewModel.Text();
          const text2 = this.viewModel.TextPart2();

          if (text2) {
            let isText1HaveErrors = text1.indexOf(disapproved) > -1;
            let isText2HaveErrors = text2.indexOf(disapproved) > -1;

            if (!isText1HaveErrors && !isText2HaveErrors) {
              // if (text1 + text2 contain disapproved text), show errors on all fields.
              isText1HaveErrors = true;
              isText2HaveErrors = true;
            }

            if (isText2HaveErrors) {
              if (isText1HaveErrors) {
                const { message, property, code, disapprovedText, isAppealable } = error;

                // If (text1 + text2) contain disapproved text, show errors on all fields.
                return [...memo, error, _.extend(new CampaignEditorialError(message, property, code, disapprovedText, isAppealable), { property: 'TextPart2' })];
              } else {
                // only have error in text2.
                error.property = 'TextPart2';
              }
            }
          }
        }

        // This mapping is for editorial errors. Once editorial is on boarded, will remove following mapping. See bug 576573.
        error.property = fieldMapping[error.property] || error.property;
        return [...memo, error];
      }
      return [...memo, error];
    }, []);

    this.viewModel.errors.add(parsedErrors);
    const errorFields = _.compact(['TrackingUrlTemplate', 'UrlCustomParameters', 'FinalUrlSuffix']);
    let errorObject = _.object(errorFields, _.map(errorFields, (key) => {
      return this.viewModel[key].errors();
    }));

    this.urlOptions.handleApiErrors(errorObject);
  }

  clearApiErrors() {
    this.viewModel.errors.clear(isCampaignApiError);
    this.urlOptions.clearApiErrors();
  }

  /**
   * Registers ad customizers helpers to listen for key/mouse events on a number of form fields to pop themselves up when applicable
   * @returns {undefined}
   */
  enableAdCustomizersHelpersForEXTA() {
    const customizableFields = ['TitlePart1', 'TitlePart2', 'Path1', 'Path2', 'Text'];

    if (IsExpandedTextAdV2Enabled) {
      customizableFields.push('TitlePart3', 'TextPart2');
    }
    enableAdCustomizersHelpers(this, customizableFields, nameAttrs, 'ExpandedTextAd');
  }
}
